import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { getLenis } from './core';

/**
 * Global scroll-position policy for every route in the app:
 *  - A fresh navigation (PUSH/REPLACE) with no hash starts at the top —
 *    that's the bug this fixes: SPA route changes don't reset scroll on
 *    their own, so the next page used to open wherever the last one left off.
 *  - A fresh navigation with a hash scrolls to that element once it exists
 *    (lazy routes/sections may not have mounted yet, so this retries briefly).
 *  - Back/forward (POP) restores the exact scroll position that page had.
 *
 * The landing page owns its own hash-scroll effect (LandingPage.tsx) because
 * its sections are idle-gated for LCP — this manager skips hash-handling
 * there so the two don't race each other.
 */

const positions = new Map<string, number>();

function moveTo(y: number) {
  const lenis = getLenis();
  if (lenis) lenis.scrollTo(y, { immediate: true });
  else window.scrollTo({ top: y, behavior: 'auto' });
}

/** Scroll `el` into view, then reassert a few times as the page settles.
 *  A freshly-mounted page still has GSAP ScrollTrigger reveals wiring up
 *  (and fonts/images arriving) — those can trigger a ScrollTrigger.refresh
 *  a beat later and silently knock the scroll position back to 0. Checking
 *  the element is still where we put it — and re-scrolling if not — makes
 *  this robust without needing to chase every possible cause of that reflow. */
function settle(el: HTMLElement) {
  const go = () => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(el, { immediate: true });
    else el.scrollIntoView({ behavior: 'auto', block: 'start' });
  };
  go();
  [150, 400, 800, 1400].forEach((delay) => {
    window.setTimeout(() => {
      if (Math.abs(el.getBoundingClientRect().top) > 4) go();
    }, delay);
  });
}

function scrollToHash(hash: string) {
  const id = hash.slice(1);
  if (!id) return;
  let tries = 0;
  const attempt = () => {
    const el = document.getElementById(id) ?? document.querySelector(hash);
    if (el) {
      settle(el as HTMLElement);
    } else if (tries++ < 30) {
      window.setTimeout(attempt, 100);
    }
  };
  attempt();
}

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Continuously snapshot scroll position under the current history key so
  // a later POP to it has something to restore.
  useEffect(() => {
    const key = location.key;
    const onScroll = () => positions.set(key, window.scrollY);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.key]);

  useEffect(() => {
    if (navType === 'POP') {
      const y = positions.get(location.key) ?? 0;
      // Route swap hasn't painted yet on this tick — wait one frame.
      requestAnimationFrame(() => moveTo(y));
      return;
    }

    if (location.hash) {
      if (location.pathname === '/') return; // LandingPage handles its own.
      scrollToHash(location.hash);
      return;
    }

    moveTo(0);
  }, [location.pathname, location.hash, location.key, navType]);

  return null;
}
